/**
 * #MT-7546968 — Countback Picking: detalhe, liberação/cancelamento em lote e relatórios.
 * #MT-699B225 — evidência: a grade lista o histórico completo (todos os status).
 */

import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import dialog from '../../versa/dialog.js';
import record from '../../versa/record.js';
import pdf from '../../versa/pdf.js';
import { notify } from '../../versa/msgbox.js';
import view from '../views/output-picking-countbacks.js';
import detail_form from '../forms/output-picking-countback-detail.js';
import report_form from '../forms/output-picking-countbacks-report.js';
import * as types from '../types.js';

/** Teto de registros que a grade consegue juntar/ordenar com várias empresas. */
const MAX_MERGE = 500;

function isCountQuery(query) {
	const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
	return qs.get('group') === 'count';
}

function pageBounds(query) {
	const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
	const limit = Number(qs.get('limit') || 50);
	const offset = Number(qs.get('offset') || 0);
	return {
		limit: Number.isFinite(limit) ? limit : 50,
		offset: Number.isFinite(offset) ? offset : 0,
	};
}

function downloadFile(base64Content, filename, mimeType) {
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: mimeType });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'arquivo';
	a.click();
	URL.revokeObjectURL(a.href);
}

/** Campo do filtro (fieldspec) → parâmetro da API de evidência. */
const FILTER_FIELD_TO_PARAM = {
	output_id: 'output_id',
	order_id: 'order_id',
	product_sku: 'sku',
	lot_number: 'lot',
	location_code: 'location',
	operator_name: 'operator',
	status: 'status',
};

/**
 * Converte o filtro da grade ({field-N, operator-N, value-N}) em filtros da API.
 * @param {object} f Filtro do crud.
 * @returns {{company_id: string, params: object}}
 */
function filtersFromCrudFilter(f) {
	const params = {};
	let company_id = '';
	for (const key of Object.keys(f || {})) {
		const match = /^field-(\d+)$/.exec(key);
		if (!match) continue;
		const idx = match[1];
		const field = f[`field-${idx}`];
		const operator = f[`operator-${idx}`] || 'eq';
		const value = String(f[`value-${idx}`] ?? '').trim();
		if (!field || !value) continue;
		if (field === 'company_id') {
			company_id = value;
			continue;
		}
		if (field === 'created_on') {
			if (operator === 'ge' || operator === 'gt') params.date_from = value;
			else if (operator === 'le' || operator === 'lt') params.date_to = value;
			else if (operator === 'eq') {
				params.date_from = value;
				params.date_to = value;
			}
			continue;
		}
		const param = FILTER_FIELD_TO_PARAM[field];
		if (param) params[param] = value;
	}
	return { company_id, params };
}

function groupIdsByCompany(selected) {
	const map = new Map();
	for (const row of selected || []) {
		const cid = Array.isArray(row) ? row[0] : row?.company_id;
		const id = Number(Array.isArray(row) ? row[1] : row?.id);
		if (!cid || !Number.isFinite(id)) continue;
		if (!map.has(cid)) map.set(cid, []);
		map.get(cid).push(id);
	}
	return map;
}

function askNoteDialog({ title, help, confirmLabel, onConfirm }) {
	const html = `
		<div class="msgbox" style="min-width:360px;max-width:520px">
			<div class="mb-title">${title}</div>
			<div class="mb-text">
				<p style="margin:0 0 0.75rem">${help}</p>
				<label>Observação (obrigatória)
					<textarea id="cb_batch_note" rows="3" style="width:100%"></textarea>
				</label>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y">${confirmLabel}</button>
		</div>
	`;
	const dlg = dialog.create(html, async (key) => {
		if (key === 'Escape' || key === 'N') {
			dlg.destroy();
			return;
		}
		if (key === 'Y') {
			const note = (document.getElementById('cb_batch_note')?.value || '').trim();
			if (!note) {
				notify('Informe a observação.', 'Countback');
				return;
			}
			dlg.destroy();
			await onConfirm(note);
		}
	}, /Escape|N|Y/);
}

const outputPickingCountbacks = function() {
	// #MT-699B225 — padrão: evidência (histórico). Alterna para fila de auditoria.
	let only_pendencies = false;

	const countbacks_crud = crud.create({
		async loadBaseData() {
			const companies = await api.call(api.list_companies, this.queryAll());
			await db.set('companies', companies || []).catch(() => {});
		},

		/** Empresas no escopo do filtro (todas quando sem filtro de empresa). */
		async scopedCompanies(company_id) {
			let companies = await db.list('companies').catch(() => []);
			if (!companies?.length) {
				// Sem a lista de empresas não há como consultar o histórico (a rota é por
				// empresa). Tenta de novo e avisa — senão a grade fica vazia sem explicação.
				companies = await api.call(api.list_companies, this.queryAll()).catch(() => []);
				if (companies?.length) await db.set('companies', companies).catch(() => {});
			}
			if (!companies?.length) {
				notify(
					'Não foi possível carregar as empresas. Entre novamente no sistema; '
					+ 'se continuar, peça ao administrador a permissão de Empresas.',
					'Countback',
				);
				return [];
			}
			if (!company_id) return companies;
			return companies.filter((c) => String(c.id) === String(company_id));
		},

		async queryData(query) {
			const { company_id, params } = filtersFromCrudFilter(this.filter);
			const companies = await this.scopedCompanies(company_id);
			if (!companies.length)
				return isCountQuery(query) ? 0 : [];

			const base = { ...params };
			if (only_pendencies) base.only_pendencies = 1;

			if (isCountQuery(query)) {
				let total = 0;
				for (const c of companies) {
					try {
						const resp = await api.call(api.list_output_countbacks, c.id, {
							...base,
							group: 'count',
						});
						total += Number(resp?.count || 0);
					} catch (_) {
						/* empresa sem ACL / sem Countback — ignora */
					}
				}
				return total;
			}

			const { limit, offset } = pageBounds(query);
			// Uma empresa: pagina no servidor. Várias: junta as páginas e ordena aqui,
			// o que só é possível até MAX_MERGE registros.
			const single = companies.length === 1;
			if (!single && offset + limit > MAX_MERGE) {
				notify(
					`Para navegar além de ${MAX_MERGE} registros, filtre por empresa.`,
					'Countback',
				);
				return [];
			}
			const all = [];
			for (const c of companies) {
				try {
					const resp = await api.call(api.list_output_countbacks, c.id, {
						...base,
						limit: single ? limit : Math.min(offset + limit, MAX_MERGE),
						offset: single ? offset : 0,
					});
					const items = Array.isArray(resp?.items) ? resp.items : [];
					for (const it of items) {
						all.push({
							...it,
							company_id: c.id,
							company_nick: c.nick || c.name || c.id,
						});
					}
				} catch (e) {
					// empresa sem ACL / sem Countback — não interrompe as demais
					console.warn('Countback: falha ao listar empresa', c.nick || c.id, e);
				}
			}
			all.sort((a, b) => String(b.created_on || '').localeCompare(String(a.created_on || '')));
			return single ? all : all.slice(offset, offset + limit);
		},

		async loadRecord(rawId) {
			try {
				const [companyId, countbackId] = JSON.parse(rawId);
				const resp = await api.call(api.get_output_countback, companyId, countbackId);
				const item = resp?.item || {};
				const history = Array.isArray(resp?.history) ? resp.history : [];
				if (!item.id) {
					notify('Ocorrência não encontrada.', 'Countback');
					return null;
				}
				return {
					company_id: companyId,
					id: item.id,
					cb_company_nick: item.company_nick || '',
					cb_product_sku: item.product_sku || '',
					cb_product_name: item.product_name || '',
					cb_status: item.status || '',
					cb_picked_quantity: item.picked_quantity ?? '',
					cb_expected_remaining_quantity: item.expected_remaining_quantity ?? '',
					cb_informed_remaining_quantity: item.informed_remaining_quantity ?? '',
					cb_difference_quantity: item.difference_quantity ?? '',
					cb_output_id: item.output_id ?? '',
					cb_order_id: item.order_id ?? '',
					cb_lot_number: item.lot_number || '',
					cb_location_code: item.location_code || '',
					cb_operator_name: item.operator_name || '',
					cb_created_on: item.created_on
						? new Date(item.created_on).toLocaleString('pt-BR')
						: '',
					cb_attempt_number: item.attempt_number ?? '',
					cb_resolution_note: item.resolution_note || '',
					cb_resolved_by_name: item.resolved_by_name || '',
					cb_resolved_at: item.resolved_at
						? new Date(item.resolved_at).toLocaleString('pt-BR')
						: '',
					cb_history_json: JSON.stringify(history),
				};
			} catch (e) {
				notify(e?.message || String(e), 'Erro ao abrir detalhe Countback');
				return null;
			}
		},

		async saveRecord() {
			return false;
		},

		async afterLoad() {
			window.__sialReleaseCountback = (companyId, countbackId) => {
				this.releaseCountback(companyId, countbackId);
			};
		},

		releaseCountback(companyId, countbackId) {
			askNoteDialog({
				title: 'Liberar Countback',
				help: 'Libera a continuidade da separação no Coletor para esta ocorrência. Não altera estoque.',
				confirmLabel: 'Liberar',
				onConfirm: async (note) => {
					try {
						await api.call(api.release_output_countback, companyId, countbackId, {
							resolution_note: note,
						});
						notify('Countback liberado.', 'Countback');
						this.loadPage();
					} catch (e) {
						notify(e?.message || String(e), 'Erro ao liberar Countback');
					}
				},
			});
		},

		async releaseSelectedBatch() {
			if (!this.ensureSelection('Liberar selecionados')) return;
			const selected = await this.getSelectedIds(['company_id', 'id']);
			if (!selected.length) return;
			if (selected.length > 50) {
				notify('Selecione no máximo 50 linhas por vez.', 'Countback');
				return;
			}
			askNoteDialog({
				title: 'Liberar selecionados',
				help: `Aprovação em lote de <b>${selected.length}</b> pendência(s). O Coletor poderá retomar o Countback nesses itens.`,
				confirmLabel: 'Liberar lote',
				onConfirm: async (note) => {
					const byCompany = groupIdsByCompany(selected);
					let ok = 0;
					let skip = 0;
					let fail = 0;
					try {
						for (const [companyId, ids] of byCompany.entries()) {
							const resp = await api.call(api.release_output_countback_batch, companyId, {
								ids,
								resolution_note: note,
							});
							ok += Number(resp?.released_count || 0);
							skip += Number(resp?.skipped_count || 0);
							fail += Number(resp?.failed_count || 0);
						}
						notify(
							`Liberados: ${ok}. Ignorados: ${skip}. Falhas: ${fail}.`,
							'Countback — liberação em lote',
						);
						this.clearGridSelection?.();
						this.loadPage();
					} catch (e) {
						notify(e?.message || String(e), 'Erro na liberação em lote');
					}
				},
			});
		},

		async cancelSelectedBatch() {
			if (!this.ensureSelection('Bloquear / cancelar selecionados')) return;
			const selected = await this.getSelectedIds(['company_id', 'id']);
			if (!selected.length) return;
			if (selected.length > 50) {
				notify('Selecione no máximo 50 linhas por vez.', 'Countback');
				return;
			}
			askNoteDialog({
				title: 'Bloquear / cancelar selecionados',
				help: `Encerra <b>${selected.length}</b> pendência(s) como <b>CANCELADO</b> (não libera o Coletor). Use quando a auditoria decide não autorizar continuidade.`,
				confirmLabel: 'Cancelar lote',
				onConfirm: async (note) => {
					const byCompany = groupIdsByCompany(selected);
					let ok = 0;
					let skip = 0;
					let fail = 0;
					try {
						for (const [companyId, ids] of byCompany.entries()) {
							const resp = await api.call(api.cancel_output_countback_batch, companyId, {
								ids,
								resolution_note: note,
							});
							ok += Number(resp?.cancelled_count || 0);
							skip += Number(resp?.skipped_count || 0);
							fail += Number(resp?.failed_count || 0);
						}
						notify(
							`Cancelados: ${ok}. Ignorados: ${skip}. Falhas: ${fail}.`,
							'Countback — bloqueio em lote',
						);
						this.clearGridSelection?.();
						this.loadPage();
					} catch (e) {
						notify(e?.message || String(e), 'Erro no cancelamento em lote');
					}
				},
			});
		},

		async openReportModal() {
			const crudRef = this;
			record.edit(report_form, async (data) => {
				const format = data.format || 'pdf';
				const useSelection = data.use_selection === true
					|| data.use_selection === '1'
					|| data.use_selection === 'on';
				let byCompany = new Map();
				if (useSelection) {
					const selected = await crudRef.getSelectedIds(['company_id', 'id']);
					if (!selected.length) {
						notify('Marque linhas na grade ou desmarque «usar selecionadas».', 'Countback');
						return false;
					}
					byCompany = groupIdsByCompany(selected);
				} else {
					const companies = await db.list('companies').catch(() => []);
					for (const c of companies || []) byCompany.set(c.id, null);
				}

				// #MT-699B225 — o export herda os filtros da grade; o modal sobrepõe.
				const { params: gridParams } = filtersFromCrudFilter(crudRef.filter);

				try {
					let generated = 0;
					let emptyCompanies = 0;
					for (const [companyId, ids] of byCompany.entries()) {
						const body = {
							...gridParams,
							format,
							only_pendencies: only_pendencies ? 1 : '',
							status: data.status || gridParams.status || '',
							output_id: data.output_id || gridParams.output_id || '',
							date_from: data.date_from || gridParams.date_from || '',
							date_to: data.date_to || gridParams.date_to || '',
						};
						if (ids) body.ids = ids;
						const resp = await api.call(api.report_output_countbacks, companyId, body);
						if (resp?.empty) {
							emptyCompanies += 1;
							continue;
						}
						if (resp?.pdf) {
							pdf(resp.pdf);
							generated += 1;
						} else if (resp?.csv) {
							downloadFile(
								resp.csv,
								resp.filename || 'countback.csv',
								'text/csv;charset=utf-8',
							);
							generated += 1;
						} else if (resp?.xlsx) {
							downloadFile(
								resp.xlsx,
								resp.filename || 'countback.xlsx',
								'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
							);
							generated += 1;
						}
					}
					if (generated > 0) {
						notify(
							generated === 1
								? 'Relatório gerado.'
								: `${generated} relatórios gerados.`,
							'Countback',
						);
						return true;
					}
					notify(
						emptyCompanies
							? 'Nenhum registro de Countback para os filtros informados.'
							: 'Não foi possível gerar o relatório.',
						'Countback',
					);
					return false;
				} catch (e) {
					notify(e?.message || String(e), 'Erro ao gerar relatório');
					return false;
				}
			}, {}, false);
		},
	});

	const fieldspecs = [
		{ index: 0, field: 'company_id', label: 'Empresa', types: [types.datalist()], store: 'companies', labels: ['nick'] },
		{ index: 1, field: 'output_id', label: 'Saída', types: [types.number(0)] },
		{ index: 2, field: 'status', label: 'Status', types: [], title: 'OK, DIVERGENTE, BLOQUEADO_AUDITORIA, LIBERADO_GESTOR ou CANCELADO' },
		{ index: 3, field: 'order_id', label: 'Pedido', types: [types.number(0)] },
		{ index: 4, field: 'product_sku', label: 'SKU', types: [types.name()], title: 'Código SKU do produto (Contém)' },
		{ index: 5, field: 'lot_number', label: 'Lote', types: [types.name()] },
		{ index: 6, field: 'location_code', label: 'Posição', types: [types.name()], title: 'Endereço da posição (ex.: 1-C-20-4)' },
		{ index: 7, field: 'operator_name', label: 'Operador', types: [types.name()] },
		{ index: 8, field: 'created_on', label: 'Data/hora', types: [types.datetime()], title: 'Data do Countback' },
	];

	const operations = [
		{ hash: '#release-countback-batch', label: 'Liberar&nbsp;selecionados' },
		{ hash: '#cancel-countback-batch', label: 'Bloquear&nbsp;/&nbsp;cancelar&nbsp;selecionados' },
		{ hash: '#toggle-countback-pendencies', label: 'Evidência&nbsp;/&nbsp;só&nbsp;pendências' },
	];
	const reports = [
		{ hash: '#export-countbacks', label: 'Exportar&nbsp;(PDF/CSV/Excel)' },
	];

	router.set('#release-countback-batch', () => countbacks_crud.releaseSelectedBatch());
	router.set('#cancel-countback-batch', () => countbacks_crud.cancelSelectedBatch());
	router.set('#export-countbacks', () => countbacks_crud.openReportModal());
	router.set('#toggle-countback-pendencies', () => {
		only_pendencies = !only_pendencies;
		notify(
			only_pendencies
				? 'Mostrando só as pendências abertas de auditoria.'
				: 'Mostrando a evidência: histórico de todos os Countbacks.',
			'Countback',
		);
		countbacks_crud.page_number = 1;
		countbacks_crud.clearGridSelection?.();
		countbacks_crud.loadPage();
	});

	countbacks_crud.run(
		'output-picking-countbacks',
		'Countback Picking',
		document.getElementById('workarea'),
		view,
		detail_form,
		fieldspecs,
		operations,
		reports,
		{ default_columns: '0,1,2' },
	);

	window.__sialReleaseCountback = (companyId, countbackId) => {
		countbacks_crud.releaseCountback(companyId, countbackId);
	};
};

export default outputPickingCountbacks;
